import { useCallback, useState } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useBisWaitlistSmsWebMwebExperiment from '@src/quince-site-common/src/hooks/use-bis-waitlist-sms-web-mweb-experiment'
import { getJoinWaitlistPhoneNumber } from '@src/quince-site-common/src/utils/join-waitlist-phone-storage'
import { isWaitlistSmsModalSuppressed } from '@src/quince-site-common/src/utils/waitlist-sms-suppression'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

interface UseWaitlistSmsModalParams {
  product: Product | undefined
  selectedOptions: ProductVariantOption[] | undefined
}

export const useWaitlistSmsModal = ({ product, selectedOptions }: UseWaitlistSmsModalParams) => {
  const [showSmsModal, setShowSmsModal] = useState(false)
  const [joinedEmail, setJoinedEmail] = useState('')
  const isBisWaitlistSmsEnabled = useBisWaitlistSmsWebMwebExperiment()

  const trackSmsEnrollment = (phoneNumber: string, email: string) => {
    if (!product) return
    const variants = productHelper.getVariantsForOptions(product, selectedOptions ?? [])
    const svid = variants[0]?.svid
    if (!svid) return
    trackEvent(TELEMETRY_EVENT_NAMES.JOIN_THE_WAITLIST_SMS, {
      phoneNumber,
      email,
      productId: product.spid,
      variantId: svid,
      context: {
        productProperties: getProductPropertiesForTracking(product, svid.toString()),
      },
    })
  }

  const openSmsModalIfAllowed = (email: string) => {
    if (!isBisWaitlistSmsEnabled) return
    setJoinedEmail(email)
    const storedPhone = getJoinWaitlistPhoneNumber()
    if (storedPhone) {
      trackSmsEnrollment(storedPhone, email)
      return
    }
    if (isWaitlistSmsModalSuppressed()) return
    setShowSmsModal(true)
  }

  const onSubmitPhone = useCallback(
    (phoneNumber: string) => {
      if (!product) return
      const variants = productHelper.getVariantsForOptions(product, selectedOptions ?? [])
      const svid = variants[0]?.svid
      if (!svid) return
      trackEvent(TELEMETRY_EVENT_NAMES.JOIN_THE_WAITLIST_SMS, {
        phoneNumber,
        email: joinedEmail,
        productId: product.spid,
        variantId: svid,
        context: {
          productProperties: getProductPropertiesForTracking(product, svid.toString()),
        },
      })
    },
    [product, selectedOptions, joinedEmail],
  )

  return {
    isBisWaitlistSmsEnabled,
    showSmsModal,
    setShowSmsModal,
    openSmsModalIfAllowed,
    onSubmitPhone,
  }
}
